import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../../../services/productService';

export default function SearchModal({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        setIsLoading(true);
        const response = await productService.getProducts();
        const products = Array.isArray(response)
          ? response
          : (response && Array.isArray(response.data)
              ? response.data
              : (response && Array.isArray(response.items) ? response.items : []));
        setAllProducts(products);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setAllProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (open && allProducts.length === 0) {
      fetchAllProducts();
    }
  }, [open, allProducts.length]);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults([]);
      return;
    }
    
    const filtered = allProducts.filter((p) => 
      (p.name && p.name.toLowerCase().includes(q)) || 
      (p.tag && p.tag.toLowerCase().includes(q)) ||
      (p.brand && p.brand.toLowerCase().includes(q)) ||
      (p.description && p.description.toLowerCase().includes(q))
    ).slice(0, 8);
    
    setResults(filtered);
  }, [query, allProducts]);

  const goToResult = (result) => {
    if (!result) return;
    const productId = result._id || result.id || result.slug;
    if (!productId) return;
    onClose();
    navigate(`/product/${encodeURIComponent(productId)}`);
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }} aria-modal="true" role="dialog">
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
        onClick={onClose}
        aria-hidden
      />
      <div style={{ position: 'absolute', left: '50%', top: '12%', transform: 'translateX(-50%)', width: 'min(920px, 94%)', background: '#fff', borderRadius: 10, boxShadow: '0 12px 40px rgba(2,6,23,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M21 21l-4.35-4.35" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="11" cy="11" r="6" stroke="#64748b" strokeWidth="1.5" />
          </svg>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
                return;
              }

              if (e.key === 'Enter') {
                e.preventDefault();
                goToResult(results[0]);
              }
            }}
            placeholder="Search products, categories..."
            style={{ flex: 1, border: 0, outline: 'none', fontSize: 16, padding: '10px 6px' }}
            aria-label="Search"
          />
          <button onClick={onClose} aria-label="Close search" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#64748b' }}>✕</button>
        </div>
        <div style={{ maxHeight: 360, overflow: 'auto', padding: 8 }}>
          {isLoading ? (
            <div style={{ padding: 16, color: '#475569' }}>Loading products...</div>
          ) : query === '' ? (
            <div style={{ padding: 16, color: '#475569' }}>Try searching for "plywood", "laminates", or "sanitary"</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 16, color: '#475569' }}>No results</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {results.map((r) => (
                <li key={r.id || r._id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 8, borderRadius: 8, cursor: 'pointer' }} onClick={() => goToResult(r)}>
                  <img 
                    src={r.imageUrl || r.image || `${process.env.PUBLIC_URL}/assets/images/product-placeholder.png`} 
                    alt="" 
                    style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6 }} 
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {r.price ? `₹${typeof r.price === 'number' ? r.price.toFixed(2) : r.price}` : 'Price on request'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
